/**
 * SwimLane Component
 * 
 * Reusable component for displaying categorized releases in a horizontal swimlane
 * with proper labeling, spacing, and timeline visualization.
 */

import React, { useState, useMemo } from 'react'
import { Box, Typography, Tooltip, useTheme } from '@mui/material'
import { CategorizedRelease, ReleaseCategory } from '../../types/release'
import { SwimlanePosition } from './swimlaneLayout'
import {
  generateSwimlaneStyles,
  generateSwimlaneLabelStyles,
  RELEASE_TIMELINE_COLORS,
  getCategoryColors
} from '../../styles/releaseTimelineStyles'
import { formatDisplayDate, getRelativeDateDescription } from '../../utils/releaseDateFiltering'
import ReleaseCard from './ReleaseCard'
import {
  calculateMultiTrackPositions,
  calculateMultiTrackHeight,
  calculateOptimalScale,
  createMultiTrackConfig,
  getTrackDistribution
} from '../../utils/dualTrackPositioning'

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface SwimLaneProps {
  /** Category for this swimlane */
  category: ReleaseCategory

  /** Releases to display in this swimlane */
  releases: CategorizedRelease[]

  /** Position configuration for this swimlane */
  position: SwimlanePosition

  /** Date range for timeline scaling */
  dateRange: { start: Date; end: Date }

  /** Timeline scale (pixels per day) for consistent resolution */
  timelineScale?: number

  /** Whether to show swimlane label */
  showLabel?: boolean

  /** Whether this swimlane is currently active/highlighted */
  isActive?: boolean

  /** Callback when a release is clicked */
  onReleaseClick?: (release: CategorizedRelease) => void

  /** Callback when swimlane is hovered */
  onSwimlaneHover?: (category: ReleaseCategory, isHovered: boolean) => void

  /** Whether to enable interactions */
  enableInteractions?: boolean

  /** Custom styling overrides */
  sx?: React.CSSProperties
}

// ============================================================================
// SWIMLANE COMPONENT
// ============================================================================

const SwimLane: React.FC<SwimLaneProps> = ({
  category,
  releases,
  position,
  dateRange,
  timelineScale: providedTimelineScale,
  showLabel = true,
  isActive = false,
  onReleaseClick,
  onSwimlaneHover,
  enableInteractions = true,
  sx = {}
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const categoryColors = getCategoryColors(category, isDarkMode)

  // ============================================================================
  // DUAL-TRACK POSITIONING CALCULATIONS
  // ============================================================================

  // Use provided timeline scale or calculate optimal scale for multi-track layout
  const timelineScale = useMemo(() => {
    return providedTimelineScale || calculateOptimalScale(dateRange, position.width, 1, 8)
  }, [providedTimelineScale, dateRange, position.width])

  // Create multi-track configuration
  const multiTrackConfig = useMemo(() => {
    return createMultiTrackConfig(timelineScale, category, {
      trackHeight: 55,
      trackSpacing: 10,
      minCardGap: 8,
      cardHeight: 45
    })
  }, [timelineScale, category])

  // Calculate multi-track positions for all releases
  const positionedReleases = useMemo(() => {
    return calculateMultiTrackPositions(releases, dateRange, category, {
      trackHeight: 55,
      trackSpacing: 5, // Reduced for more compact layout
      minCardGap: 8,
      cardHeight: 45,
      timelineScale,
      minCardWidth: 80
    }, position.width) // Pass actual swimlane width
  }, [releases, dateRange, category, timelineScale, position.width])

  // Calculate required swimlane height for multi-track layout
  const requiredHeight = useMemo(() => {
    return calculateMultiTrackHeight(category)
  }, [category])

  // Get track distribution for debugging/stats
  const trackStats = useMemo(() => {
    return getTrackDistribution(positionedReleases)
  }, [positionedReleases])

  const swimlaneStyles = useMemo(() => ({
    position: 'absolute' as const,
    top: position.top,
    left: position.left,
    width: position.width,
    height: requiredHeight, // Use calculated dual-track height
    backgroundColor: categoryColors.light,
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
    ...(isActive || isHovered ? {
      backgroundColor: categoryColors.light,
      boxShadow: `0 2px 8px ${categoryColors.primary}15`
    } : {}),
    ...sx
  }), [category, isActive, isHovered, position, sx, categoryColors])
  
  const labelStyles = useMemo(() => ({
    position: 'absolute' as const,
    top: position.top,
    left: 16, // Fixed left position
    width: 80, // Fixed width for labels
    height: position.height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingLeft: '12px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: categoryColors.text,
    backgroundColor: 'transparent'
  }), [category, position, categoryColors])
  
  // Filter releases that have valid dates for timeline display (for stats only)
  const timelineReleases = useMemo(() => {
    return releases.filter(release => {
      if (!release.releaseDate) return false
      const releaseDate = new Date(release.releaseDate)
      return !isNaN(releaseDate.getTime()) &&
             releaseDate >= dateRange.start &&
             releaseDate <= dateRange.end
    })
  }, [releases, dateRange])
  
  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  const handleMouseEnter = () => {
    if (!enableInteractions) return
    setIsHovered(true)
    onSwimlaneHover?.(category, true)
  }
  
  const handleMouseLeave = () => {
    if (!enableInteractions) return
    setIsHovered(false)
    onSwimlaneHover?.(category, false)
  }
  
  const handleReleaseClick = (release: CategorizedRelease) => {
    if (!enableInteractions) return
    onReleaseClick?.(release)
  }
  
  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  // Dual-track positioning is now handled by the dualTrackPositioning utility
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <>
      {/* Swimlane Label */}
      {showLabel && (
        <Tooltip 
          title={`${category} releases (${releases.length} total, ${timelineReleases.length} in timeline)`}
          placement="left"
        >
          <Box sx={labelStyles}>
            <Typography
              variant="body2"
              sx={{
                color: categoryColors.text,
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              {category}
            </Typography>
          </Box>
        </Tooltip>
      )}
      
      {/* Swimlane Container */}
      <Box
        sx={swimlaneStyles}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="region"
        aria-label={`${category} release timeline`}
        aria-describedby={`swimlane-${category.toLowerCase()}-description`}
      >
        {/* Hidden description for screen readers */}
        <Box
          id={`swimlane-${category.toLowerCase()}-description`}
          sx={{ position: 'absolute', left: '-9999px' }}
        >
          {`Timeline showing ${timelineReleases.length} ${category} releases from ${formatDisplayDate(dateRange.start)} to ${formatDisplayDate(dateRange.end)}`}
        </Box>
        
        {/* Release Cards - Dual-Track Layout */}
        {positionedReleases.map(({ release, position: releasePos }) => {
          return (
            <ReleaseCard
              key={release.id}
              release={release}
              position={{
                left: releasePos.left,
                width: releasePos.width,
                top: releasePos.top
              }}
              swimlaneHeight={multiTrackConfig.trackHeight}
              onClick={() => handleReleaseClick(release)}
              enableInteractions={enableInteractions}
              showDetails={true}
              trackInfo={{
                trackNumber: releasePos.track + 1, // Convert 0-based to 1-based
                totalTracks: multiTrackConfig.trackCount
              }}
            />
          )
        })}
        
        {/* Empty State */}
        {timelineReleases.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'text.secondary',
              opacity: 0.7,
              fontSize: '0.8rem',
              fontStyle: 'italic',
              textAlign: 'center'
            }}
          >
            No releases in timeline range
          </Box>
        )}
        
        {/* Hover Indicator */}
        {isHovered && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: `2px solid ${categoryColors.primary}`,
              borderRadius: '4px',
              pointerEvents: 'none',
              opacity: 0.8
            }}
          />
        )}
      </Box>
    </>
  )
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

export default React.memo(SwimLane)

// ============================================================================
// COMPONENT DISPLAY NAME
// ============================================================================

SwimLane.displayName = 'SwimLane'
